import { createClient } from '@supabase/supabase-js';

const supabaseUrl = "https://cnpozvvusjsxkydugtlg.supabase.co";
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNucG96dnZ1c2pzeGt5ZHVndGxnIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTkyOTUxMTYsImV4cCI6MjAxNDg3MTExNn0.bRrcrO1pbKmIF7KXDJn03pTSQhx9VxYA4hGFnbFY3UA';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const DENTAL_API_URL =
  process.env.REACT_APP_DENTAL_API_URL ||
  'https://dentalexpressserver.azurewebsites.net';
if (typeof window !== 'undefined') {
  // Surface the active API target once at startup so it's obvious in the
  // console when the dashboard is pointed at a non-production server.
  // eslint-disable-next-line no-console
  console.info(`[common] DENTAL_API_URL=${DENTAL_API_URL}`);
}

export function handleAuthFailure() {
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
}

const refreshAccessToken = async () => {
  try {
    const { data: currentSession } = await supabase.auth.getSession();
    if (!currentSession?.session) {
      return null;
    }

    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error("Failed to refresh token:", error);
      handleAuthFailure();
      return null;
    }

    if (data.session) {
      localStorage.setItem('jwtToken', data.session.access_token);
      if (data.session.refresh_token) {
        localStorage.setItem('refreshToken', data.session.refresh_token);
      }
      return data.session;
    }
    return null;
  } catch (error) {
    console.error("Error in refreshAccessToken:", error);
    return null;
  }
};

/**
 * Validates the current JWT token by checking expiration.
 * Returns true if valid, false if expired/missing.
 * Attempts a refresh before returning false.
 */
export async function validateSession() {
  const token = localStorage.getItem('jwtToken');
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const nowSec = Math.floor(Date.now() / 1000);

    if (payload.exp && payload.exp > nowSec) {
      return true;
    }

    // Token expired — try refreshing via Supabase
    const newSession = await refreshAccessToken();
    return !!newSession;
  } catch {
    return false;
  }
}

async function doFetchWithRetry(endpoint, options = {}, isRetry = false, baseUrl = DENTAL_API_URL) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const token = localStorage.getItem('jwtToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    const session = await supabase.auth.getSession();
    if (session.data?.session?.access_token) {
      localStorage.setItem('jwtToken', session.data.session.access_token);
      headers.Authorization = `Bearer ${session.data.session.access_token}`;
    } else {
      handleAuthFailure();
      return null;
    }
  }

  const response = await fetch(`${baseUrl}${endpoint}`, { ...options, headers });

  if (response.ok) {
    return response;
  }

  if (response.status === 401 && !isRetry) {
    console.log("Token expired, attempting refresh...");
    const newSession = await refreshAccessToken();
    if (newSession) {
      console.log("Token refreshed successfully, retrying request");
      return doFetchWithRetry(endpoint, options, true, baseUrl);
    }
    handleAuthFailure();
    throw new Error('Authentication failed - please log in again');
  }

  if (response.status === 403) {
    handleAuthFailure();
  }

  throw new Error(`API error: ${response.status} ${response.statusText}`);
}

export async function fetchFromDentalAPI(endpoint, options = {}) {
  const response = await doFetchWithRetry(endpoint, options);
  return response.json();
}

/**
 * Drop-in replacement for the existing secureFetch used in Redux slices.
 * Returns the raw Response object (callers handle .json() themselves).
 */
export async function secureFetch(url, options = {}) {
  const headers = {
    ...(options.headers || {}),
  };

  const token = localStorage.getItem('jwtToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    const session = await supabase.auth.getSession();
    if (session.data?.session?.access_token) {
      localStorage.setItem('jwtToken', session.data.session.access_token);
      headers.Authorization = `Bearer ${session.data.session.access_token}`;
    } else {
      handleAuthFailure();
      throw new Error('No active session');
    }
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    console.log("Token expired on secureFetch, attempting refresh...");
    const newSession = await refreshAccessToken();
    if (newSession) {
      const retryHeaders = {
        ...headers,
        Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      };
      return fetch(url, { ...options, headers: retryHeaders });
    }
    handleAuthFailure();
    throw new Error('Authentication failed - please log in again');
  }

  return response;
}
